import { useEffect, memo } from 'react'
import { observer } from 'mobx-react'
import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CONFIG_DDF,
  CONFIG_DDF_RCE_EDIT,
  CONFIG_SECOND_DEBTOR,
  isRceEditConfig
} from 'config/utils/config'
import { NavigateFunction } from 'react-router-dom'
import LegalHintTexts from 'texts/common/copy/legal'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { getButtonSizes } from './getButtonSizes'
import FormNavigation from './FormNavigationTemplate'
import { AgeGroup } from 'constants/components'
import useUserState from 'hooks/useUserState'
import { Button } from '@mui/material'
import { isYoungOrMiddleAgeGroup } from 'stores/helpers/AgeGroups'
import { useFFGSkipDacButton } from 'hooks/useFFGSkipDacButton'

type Props = {
  onBack?: () => void
  onNext: () => void
  navigate: NavigateFunction
}

const FFGFormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    rootStore: {
      isInIframe,
      urlParams: { backUrl },
      page: { config }
    }
  } = useRootStore()

  const {
    page: { isFirstDisplayedPage, isLastDisplayedPage, getCurrentIndex },
    navigation: { currentPageInSession, isDacPage },
    traversal: { data },
    traversalSession: { dacWidgetState }
  } = useStores()

  const { ageGroup, debtorsAge, isYoungAge, isMiddleAge } = useUserState()

  const isInRceRoute = isRceEditConfig(config.name)

  const isPayoutAccountReady = usePayoutAccountValidation(true)

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const currentIndex = getCurrentIndex()

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const isLastPage = isLastDisplayedPage(currentPageInSession)

  const backLabel =
    !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

  const isBackButtonHidden = !backLabel || isFirstPage

  const getIsContinueButtonHidden = () => {
    if (config.name === CONFIG_SECOND_DEBTOR) {
      return false
    }

    if (config.name === CONFIG_DDF) {
      if (
        (isDacPage && dacWidgetState !== 'success') ||
        (ageGroup !== AgeGroup.OLD_AGE_GROUP && isDacPage)
      ) {
        return true
      }

      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      isYoungOrMiddleAgeGroup(debtorsAge)
    )
  }

  const isContinueButtonHidden = getIsContinueButtonHidden()

  let submitLabel = isLastPage ? 'Angebote ansehen' : 'Weiter'

  const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
    trackInAmplitude(event, { origin: 'taurine', ...payload })
  }

  const onBackHandler = () => {
    const isFinalizingOnBackButton =
      isYoungAge && isDacPage && config.name !== CONFIG_DDF_RCE_EDIT

    if (isFinalizingOnBackButton) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'final_on_back_button_click',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext()
      return
    }
    onBack && onBack()
  }

  useEffect(() => {
    if (dacWidgetState === 'success') {
      trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
      onNext()
    }
  }, [dacWidgetState])

  const { isSkipDacButtonVisible, skipButtonText, onSkipDacButtonClick } =
    useFFGSkipDacButton({
      isInRceRoute,
      isMiddleAgeGroup: isMiddleAge,
      ageGroup,
      isDacPage,
      isLastPage,
      data,
      onNext
    })

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden,
      isSkipDacButtonVisible
    )

  const showDacLegalText =
    isLastPage &&
    isPayoutAccountReady &&
    isDacPage &&
    config.name === CONFIG_DDF

  return (
    <FormNavigation
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.defaultLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={sizeOfContinueButton}
      sizeOfBackButton={sizeOfBackButton}
      sizeOfSaveButton={sizeOfSaveButton}
      submitLabel={submitLabel}
      backLabel={backLabel}
      sizeOfSkipDacButton={12}
      onNextWithTracking={onNext}
      trackWithOrigin={trackWithOrigin}
      continueButtonColor={'primary'}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        isSkipDacButtonVisible ? (
          <Button
            id="test-skip-dac"
            name="test-skip-dac"
            onClick={onSkipDacButtonClick}
            color="primary"
            fullWidth
            variant={config.name === CONFIG_DDF ? 'contained' : 'outlined'}
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              height: '50px'
            }}
          >
            {skipButtonText}
          </Button>
        ) : null
      }
      showDacLegalText={showDacLegalText}
      backButtonVariant="text"
      saveButtonVariant={'outlined'}
    />
  )
})

export default memo(FFGFormNavigation)
